import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { API_URL } from '../config';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name' && /[^a-zA-Z\s]/.test(value)) {
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (err) {
      toast.error('Network error. Please try again later.');
    }
  };

  return (
    <div className="container section">
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 className="section-title" style={{ marginBottom: '1rem' }}>Get in Touch</h1>
        <p className="section-subtitle" style={{ margin: '0 auto', maxWidth: '600px' }}>
          Have a question about our wilderness tours or need help planning your next great adventure? We're here to help!
        </p>
      </div>

      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '2rem',
        backgroundColor: '#fff',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
        maxWidth: '1000px',
        margin: '0 auto'
      }}>
        {/* Contact Info (Left Side) */}
        <div style={{ 
          flex: '1 1 350px', 
          backgroundColor: '#ea580c', 
          color: 'white', 
          padding: '3rem 2rem',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>Contact Information</h3>
          <p style={{ color: '#e5e7eb', marginBottom: '3rem', lineHeight: '1.6' }}>
            Fill up the form and our team will get back to you within 24 hours.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', flexGrow: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Phone size={24} color="#a7f3d0" />
              <span style={{ fontSize: '1.1rem' }}>+91 98765 43210</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Mail size={24} color="#a7f3d0" />
              <span style={{ fontSize: '1.1rem' }}>hello@wilderness.com</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <MapPin size={24} color="#a7f3d0" />
              <span style={{ fontSize: '1.1rem', lineHeight: '1.5' }}>123 Safari Way,<br/>New Delhi, India 110001</span>
            </div>
          </div>
          
          {/* Decorative circles */}
          <div style={{ position: 'relative', height: '100px', overflow: 'hidden', marginTop: '2rem' }}>
            <div style={{ position: 'absolute', right: '-20px', bottom: '-20px', width: '150px', height: '150px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)' }}></div>
            <div style={{ position: 'absolute', right: '40px', bottom: '40px', width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.15)' }}></div>
          </div>
        </div>

        {/* Contact Form (Right Side) */}
        <div style={{ flex: '2 1 450px', padding: '3rem 2rem' }}>
          {submitted ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{ textAlign: 'center', padding: '4rem 0' }}
            >
              <div style={{ width: '80px', height: '80px', backgroundColor: '#e6f4ea', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
                <Send size={40} color="#ea580c" />
              </div>
              <h3 style={{ fontSize: '1.8rem', color: '#111', marginBottom: '1rem', fontWeight: 'bold' }}>Message Sent!</h3>
              <p style={{ color: '#5f6368', fontSize: '1.1rem' }}>Thank you for reaching out. We will get back to you shortly.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label htmlFor="name" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Your Name</label>
                  <input 
                    type="text" 
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    style={{ padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '1rem', transition: 'border-color 0.2s' }}
                    onFocus={(e) => e.target.style.borderColor = '#ea580c'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
                <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label htmlFor="email" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Your Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    style={{ padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '1rem', transition: 'border-color 0.2s' }}
                    onFocus={(e) => e.target.style.borderColor = '#ea580c'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label htmlFor="subject" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Subject</label>
                <input 
                  type="text" 
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  style={{ padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '1rem', transition: 'border-color 0.2s' }}
                  onFocus={(e) => e.target.style.borderColor = '#ea580c'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label htmlFor="message" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Message</label>
                <textarea 
                  id="message"
                  name="message"
                  required
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  style={{ padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '1rem', resize: 'vertical', transition: 'border-color 0.2s' }}
                  onFocus={(e) => e.target.style.borderColor = '#ea580c'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                ></textarea>
              </div>

              <button 
                type="submit"
                style={{
                  marginTop: '1rem',
                  padding: '1rem 2rem',
                  backgroundColor: '#ea580c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1.05rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'background-color 0.2s, transform 0.1s',
                  alignSelf: 'flex-start'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c2410c'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ea580c'}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Send Message <Send size={18} />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
